import { Badge, type BadgeTone } from './components/ui.js';
import type { BuildStatus, DeployStatus, ProjectStatus, StoryStatus } from './api/types.js';

/** Maps domain statuses to a badge tone + human label, in one place. */

const PROJECT: Record<ProjectStatus, { tone: BadgeTone; label: string }> = {
  questionnaire: { tone: 'neutral', label: 'Questionnaire' },
  generating_spec: { tone: 'progress', label: 'Generating spec' },
  spec_review: { tone: 'attention', label: 'Spec review' },
  spec_shared: { tone: 'attention', label: 'Awaiting approval' },
  spec_approved: { tone: 'progress', label: 'Spec approved' },
  generating_stories: { tone: 'progress', label: 'Generating stories' },
  building: { tone: 'progress', label: 'In progress' },
  ongoing_refinement: { tone: 'progress', label: 'Ongoing refinement' },
  archived: { tone: 'neutral', label: 'Paused' },
};

const BUILD: Record<BuildStatus, { tone: BadgeTone; label: string }> = {
  queued: { tone: 'neutral', label: 'Queued' },
  running: { tone: 'progress', label: 'Running' },
  awaiting_approval: { tone: 'attention', label: 'Awaiting approval' },
  succeeded: { tone: 'success', label: 'Succeeded' },
  failed: { tone: 'danger', label: 'Failed' },
  interrupted: { tone: 'danger', label: 'Interrupted' },
};

const STORY: Record<StoryStatus, { tone: BadgeTone; label: string }> = {
  draft: { tone: 'neutral', label: 'Draft' },
  in_review: { tone: 'neutral', label: 'In review' },
  ready: { tone: 'attention', label: 'Ready for approval' },
  approved: { tone: 'progress', label: 'Approved' },
  building: { tone: 'progress', label: 'Building' },
  built: { tone: 'attention', label: 'In test' }, // finished building, awaiting testing
  accepted: { tone: 'success', label: 'Built' }, // tested and signed off ("done")
};

const DEPLOY: Record<DeployStatus, { tone: BadgeTone; label: string }> = {
  none: { tone: 'neutral', label: 'Not deployed' },
  provisioning: { tone: 'progress', label: 'Provisioning' },
  building: { tone: 'progress', label: 'Deploying' },
  live: { tone: 'success', label: 'Live' },
  failed: { tone: 'danger', label: 'Deploy failed' },
};

export function ProjectStatusBadge({ status }: { status: ProjectStatus }) {
  const s = PROJECT[status] ?? { tone: 'neutral' as const, label: status };
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

export function BuildStatusBadge({ status }: { status: BuildStatus }) {
  const s = BUILD[status] ?? { tone: 'neutral' as const, label: status };
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

export function StoryStatusBadge({ status }: { status: StoryStatus }) {
  const s = STORY[status] ?? { tone: 'neutral' as const, label: status };
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

export function DeployStatusBadge({ status }: { status: DeployStatus }) {
  const s = DEPLOY[status] ?? { tone: 'neutral' as const, label: status };
  return <Badge tone={s.tone}>{s.label}</Badge>;
}
